import { InjectionToken, type Provider } from '@angular/core';

export interface AppShellConfig {
  /** 모바일로 취급할 CDK breakpoint 목록. 기본 `[Breakpoints.Handset, Breakpoints.TabletPortrait]`. */
  readonly mobileBreakpoints?: readonly string[];
  /** 셸이 `<app-footer />`를 렌더할지 여부. 기본 `true`. */
  readonly renderFooter?: boolean;
  /** 사이드바 너비 Tailwind 클래스. 기본 `'w-64'`. */
  readonly sidenavWidthClass?: string;
  /** 브랜드 로고 이미지 src. 라이트·(logoDark 없으면) 다크 공통. */
  readonly brandLogo?: string;
  /** 다크모드 전용 브랜드 로고 src. 없으면 `brandLogo`를 그대로 쓴다. */
  readonly brandLogoDark?: string;
  /** 로고만 있고 텍스트가 없을 때 쓰는 대체 텍스트. 기본 `'홈'`. */
  readonly brandLogoAlt?: string;
  /**
   * 브랜드 텍스트 기본값. 컴포넌트 `title` 입력이 있으면 그쪽이 우선한다. `title` 입력과
   * 이름을 분리한 이유는 `ShellTitleStrategy`가 root provider라 컴포넌트 입력을 볼 수 없어,
   * document.title 조합에 쓸 값은 반드시 설정 레벨에 있어야 하기 때문이다.
   */
  readonly brandText?: string;
  /**
   * 콘텐츠 스트립에 다크모드 토글을 렌더할지 여부. 기본 `false`(옵트인) —
   * 앱이 다크모드 CI lint(`tools/ci/lint-dark-mode-friendly.mjs`)의 기존 위반을
   * 다 고치기 전에 토글을 켜면 페이지가 시각적으로 깨진 채 노출된다.
   */
  readonly showThemeToggle?: boolean;
  /**
   * 이 경로 접두사로 시작하는 라우트는 `ThemeService`가 localStorage에 저장된 명시적
   * 선택을 읽지 않고 OS `prefers-color-scheme`만 따른다 — OIDC authorize/consent
   * 화면, RP가 window.open()으로 여는 팝업처럼 완전히 다른 origin/제3자 컨텍스트에서
   * 열릴 수 있는 화면이 대상이다. 이 origin에 과거 저장된(그 방문과 무관한) 선택을
   * 그대로 노출하면 호스트 페이지 테마와 어긋나 보일 수 있어, 문맥과 무관한 환경 신호인
   * OS 설정만 참고하게 한다. 기본값 없음(전부 저장값 존중).
   */
  readonly themeIgnoresStoredRoutePrefixes?: readonly string[];
  /** `ShellTitleStrategy`가 `document.title`에 `brandText`/라우트 title과 함께 덧붙이는 접미사. */
  readonly documentTitleSuffix?: string;
  /** `ShellTitleStrategy`가 라우트 title과 `brandText`를 이을 때 쓰는 구분자. 기본 `' · '`. */
  readonly documentTitleSeparator?: string;
}

export const APP_SHELL_CONFIG = new InjectionToken<AppShellConfig>('APP_SHELL_CONFIG');

export function provideAppShell(config: AppShellConfig = {}): Provider {
  return { provide: APP_SHELL_CONFIG, useValue: config };
}
